import { IGameSystem } from '../../core/IGameSystem';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { GAMEPLAY_CONFIG } from '../../../shared/constants/GameplayConfig';
import { gameLogger } from '../../../utils/GameLogger';
import type { GamepadActivityAction } from '../input/GamepadInputSource';
import { bootSearchParams } from '../../../config/boot/bootUrl';

/**
 * TutorialSystem — the tomb → village → field on-ramp beat engine.
 *
 * Eight contextual, just-in-time beats split across the on-ramp
 * (docs/design/levels/on-ramp-design.md §2). Each beat is one sentence,
 * never blocks play, and shows one-at-a-time (later triggers queue behind
 * the active beat's dismissal).
 *
 * Lifecycle:
 *  - Arms when `village_arrival` or `first_field` loads
 *    or the per-browser done-flag is set (`helix_ftue_completed` — a helix_*
 *    localStorage key, so the /intro `?reset=1` fresh-character wipe replays
 *    the FTUE exactly like everything else).
 *  - `tutorial:step_changed` / `tutorial:objective_completed` drive the UI
 *    (TutorialOverlay); InteractionContext flips `tutorialActive` on/off from
 *    the same events.
 *  - Tomb travel remains inside the session without surfacing prompts.
 *  - Leaving the on-ramp mid-FTUE ends the tutorial quietly.
 */

export enum TutorialStep {
  MOVE = 'move',
  ATTACK = 'attack',
  FIRST_KILL = 'first_kill',
  FIRST_LOOT = 'first_loot',
  LEVEL_UP = 'level_up',
  FIRST_ABILITY = 'first_ability',
  TALK_TO_NPC = 'talk_to_npc',
  TAKE_QUEST = 'take_quest',
  COMPLETED = 'completed',
}

export interface TutorialObjective {
  id: TutorialStep;
  title: string;
  /** One sentence. `**text**` segments render bold in the overlay. */
  description: string;
  isCompleted: boolean;
}

type BeatPhase = 'pending' | 'queued' | 'active' | 'done';

interface BeatDef {
  title: string;
  description: string;
  /** When set, the beat auto-dismisses this many ms after it shows. */
  autoDismissMs?: number;
}

const TOMB_MAP_ID = 'tomb_waking';
const VILLAGE_MAP_ID = 'village_arrival';
const FIELD_MAP_ID = 'first_field';
const TUTORIAL_ARM_MAP_IDS = [VILLAGE_MAP_ID, FIELD_MAP_ID] as const;
const ON_RAMP_MAP_IDS = [TOMB_MAP_ID, VILLAGE_MAP_ID, FIELD_MAP_ID] as const;
/** The first NPC — the researcher waiting in the village square. */
const TOMAS_NPC_ID = 'tomas';
/** MOVE nudge: fires this long after arming if the player hasn't moved. */
const MOVE_NUDGE_DELAY_MS = 2500;
/** ATTACK beat trigger: hostile mob within this radius (design §2, ~180px). */
const ATTACK_TRIGGER_RADIUS_PX = 180;
/** Proximity/trigger polling cadence — beats are prompts, not physics. */
const POLL_INTERVAL_MS = 250;
/** Wiped by the /intro `?reset=1` fresh-character wipe (helix_* prefix). */
const FTUE_DONE_KEY = 'helix_ftue_completed';
const GAMEPAD_MOVEMENT_ACTIONS: ReadonlySet<GamepadActivityAction> = new Set([
  'move_left',
  'move_right',
  'move_up',
  'move_down',
]);

const BEAT_DEFS: Readonly<Record<Exclude<TutorialStep, TutorialStep.COMPLETED>, BeatDef>> = {
  [TutorialStep.MOVE]: {
    title: 'Move',
    description: 'Move with **WASD**, arrow keys, the **left stick**, or the **D-pad**.',
  },
  [TutorialStep.ATTACK]: {
    // Design doc says "Space" but Space is jump — basic_attack is F (v1.8).
    title: 'Attack',
    description: 'Press **F** to attack.',
  },
  [TutorialStep.FIRST_KILL]: {
    title: 'First Kill',
    description: 'You defeated it! Warden Lark is waiting at the east survey post.',
    autoDismissMs: 3000,
  },
  [TutorialStep.FIRST_LOOT]: {
    title: 'Loot',
    description: 'You found an item! Press **I** to open your inventory.',
    autoDismissMs: 6000,
  },
  [TutorialStep.LEVEL_UP]: {
    title: 'Level Up',
    description: 'You earned **5 stat points** and **3 talent points**! Open **Character (C)** for Stats and **Talents (N)** for your talent tree.',
    autoDismissMs: 6000,
  },
  [TutorialStep.FIRST_ABILITY]: {
    title: 'Ability Training',
    description: 'A new ability is available to train! Visit your class trainer in Tidewend.',
    autoDismissMs: 8000,
  },
  [TutorialStep.TALK_TO_NPC]: {
    title: 'Meet Tomas',
    description: 'Talk to **Tomas** — the researcher in the square.',
  },
  [TutorialStep.TAKE_QUEST]: {
    title: 'Take the Quest',
    description: 'Accept Tomas\'s quest to begin your adventure.',
  },
};

export class TutorialSystem implements IGameSystem {
  public readonly id = 'TutorialSystem';

  private phases = new Map<TutorialStep, BeatPhase>();
  private objectives = new Map<TutorialStep, TutorialObjective>();
  private queue: TutorialStep[] = [];
  private activeBeat: TutorialStep | null = null;
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

  private armed = false;
  private finished = false;
  private armedAtMs = 0;
  /** True once a movement input was observed (the MOVE beat's truth signal —
   *  positions are useless here, boot spawn-corrections move the player
   *  hundreds of px with zero input). */
  private movementInputSeen = false;
  private currentMapId: string | null = null;
  private lastPollAtMs = 0;
  private unsubscribers: Array<() => void> = [];
  private onMoveKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    for (const step of Object.keys(BEAT_DEFS) as Array<keyof typeof BEAT_DEFS>) {
      const def = BEAT_DEFS[step];
      this.phases.set(step, 'pending');
      this.objectives.set(step, {
        id: step,
        title: def.title,
        description: def.description,
        isCompleted: false,
      });
    }
  }

  public async initialize(): Promise<void> {
    if (this.isSuppressed()) {
      this.finished = true;
      gameLogger.info('tutorial', '[TutorialSystem] Suppressed (skipTutorial / already completed)');
      return;
    }
    this.setupListeners();
  }

  // ── Public state (TutorialOverlay pulls this on mount) ─────────────

  public getCurrentStep(): TutorialStep {
    if (this.finished) return TutorialStep.COMPLETED;
    return this.activeBeat ?? TutorialStep.COMPLETED;
  }

  /** The currently-shown beat's objective, or null when nothing is up. */
  public getActiveObjective(): TutorialObjective | null {
    if (this.finished || !this.activeBeat) return null;
    return this.objectives.get(this.activeBeat) ?? null;
  }

  public getObjectives(): TutorialObjective[] {
    return Array.from(this.objectives.values());
  }

  public isArmed(): boolean {
    return this.armed && !this.finished;
  }

  // ── Arming / suppression ───────────────────────────────────────────

  private isSuppressed(): boolean {
    try {
      if (bootSearchParams().get('skipTutorial') === '1') {
        return true;
      }
      if (typeof localStorage !== 'undefined' && localStorage.getItem(FTUE_DONE_KEY) === '1') {
        return true;
      }
    } catch { /* non-browser context → not suppressed */ }
    return false;
  }

  private setupListeners(): void {
    const bus = EventBus.getInstance();
    const sub = (unsub: () => void) => this.unsubscribers.push(unsub);

    // MOVE truth signal — a PASSIVE, observe-only movement-key listener
    // (InputSystem stays the sole keyboard HANDLER; this never consumes or
    // acts on input, same pattern as the narration skip). Positions can't be
    // trusted for this: boot spawn-corrections warp the player with zero
    // input and would silently complete the beat.
    if (typeof window !== 'undefined') {
      const MOVE_KEYS = new Set(['w', 'a', 's', 'd', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown']);
      this.onMoveKeyDown = (e: KeyboardEvent) => {
        if (!MOVE_KEYS.has(e.key?.toLowerCase?.() ?? '')) return;
        this.observeVillageMovementInput();
      };
      window.addEventListener('keydown', this.onMoveKeyDown, { passive: true });
    }

    // InputSystem owns gamepad polling and emits only rising-edge activity.
    // Observing that typed event keeps the tutorial passive while giving
    // stick and D-pad movement the same truth signal as keyboard movement.
    sub(bus.on(EVENTS.GAMEPAD_ACTIVITY, ({ action }) => {
      if (!GAMEPAD_MOVEMENT_ACTIONS.has(action)) return;
      this.observeVillageMovementInput();
    }));

    // Arm in the village (normal route) or field (direct test route), then
    // preserve state across the whole tomb/village/field chain. LEVEL_LOADED
    // is sticky, so a late-initializing system still sees the current leg.
    sub(bus.on(EVENTS.LEVEL_LOADED, (data: { id: string }) => {
      this.currentMapId = data?.id ?? null;
      if (TUTORIAL_ARM_MAP_IDS.includes(this.currentMapId as any)) {
        this.arm(this.currentMapId);
      } else if (this.armed && !this.finished && !ON_RAMP_MAP_IDS.includes(this.currentMapId as any)) {
        gameLogger.info('tutorial', '[TutorialSystem] Left on-ramp mid-FTUE — ending quietly');
        this.finish({ persist: false });
      }
    }));

    // Attack input (F / hotbar cast) dismisses ATTACK — or marks it done
    // silently if the player attacked before the nudge ever showed.
    sub(bus.on(EVENTS.PLAYER_ACTION_INTENT, (data) => {
      if (!this.armed || this.finished || !data || !this.isFieldPhase()) return;
      const payload = (data as { payload?: { attack?: boolean; secondaryAttack?: boolean; abilitySlot?: number | null } }).payload;
      const isAttackInput =
        (data.type === 'playerAction' && !!payload &&
          (payload.attack === true || payload.secondaryAttack === true || payload.abilitySlot != null)) ||
        data.action === 'castAbility';
      if (isAttackInput) this.completeBeat(TutorialStep.ATTACK);
    }));

    // FIRST_KILL — first mob death attributed to the local player.
    sub(bus.on(EVENTS.NPC_DEFEATED, (msg: { defeatedBy?: string }) => {
      if (!this.armed || this.finished || !this.isFieldPhase()) return;
      if (this.phases.get(TutorialStep.FIRST_KILL) !== 'pending') return;
      const sessionId = this.localSessionId();
      if (sessionId && msg?.defeatedBy && msg.defeatedBy !== sessionId) return;
      this.requestBeat(TutorialStep.FIRST_KILL);
    }));

    // FIRST_LOOT — first item pickup; dismissed by opening the inventory (I).
    sub(bus.on(EVENTS.NET_MESSAGE_LOOT_RECEIVED, () => {
      if (this.armed && !this.finished && this.isFieldPhase()) this.requestBeat(TutorialStep.FIRST_LOOT);
    }));

    // LEVEL_UP — first level increase (1 → 2). The prompt points to the
    // existing Quick Menu instead of inventing a stat/talent hotkey.
    const onLevelUp = (level: number | undefined) => {
      if (!this.armed || this.finished || !this.isFieldPhase()) return;
      if (typeof level === 'number' && level >= 2) this.requestBeat(TutorialStep.LEVEL_UP);
    };
    sub(bus.on(EVENTS.NET_MESSAGE_LEVEL_UP, (msg) => onLevelUp(msg?.level)));
    sub(bus.on(EVENTS.PLAYER_LEVEL_UP, (msg) => onLevelUp(msg?.level)));

    // FIRST_ABILITY — a trainer prompt while the bounded FTUE is still armed.
    // The global progression notice handles the ordinary level-3 cadence,
    // which occurs after first_hunt has normally finished this tutorial.
    sub(bus.on(EVENTS.NET_MESSAGE_ABILITY_TRAINING_AVAILABLE, (msg) => {
      if (!this.armed || this.finished || !this.isFieldPhase()) return;
      if (Array.isArray(msg?.abilityIds) && msg.abilityIds.length > 0) {
        this.requestBeat(TutorialStep.FIRST_ABILITY);
      }
    }));

    // Successful server-authoritative training completes the trainer prompt.
    sub(bus.on(EVENTS.NET_MESSAGE_LEARN_ABILITY_RESULT, (msg) => {
      if (!this.armed || this.finished || msg?.ok !== true) return;
      this.completeBeat(TutorialStep.FIRST_ABILITY);
    }));

    // TALK_TO_NPC dismiss — Tomas's dialogue opened. Reaching Tomas also
    // completes the village movement/contact beats.
    sub(bus.on(EVENTS.NPC_INTERACT, (data: { npcId: string }) => {
      if (!this.armed || this.finished || !this.isVillagePhase()) return;
      if (data?.npcId === TOMAS_NPC_ID) {
        this.completeBeat(TutorialStep.MOVE);
        this.completeBeat(TutorialStep.TALK_TO_NPC);
      }
    }));

    // TAKE_QUEST trigger — Tomas's dialogue is open with quests available.
    sub(bus.on(EVENTS.NET_MESSAGE_NPC_DIALOGUE, (payload) => {
      if (!this.armed || this.finished || !this.isVillagePhase()) return;
      if (payload?.ok === true && payload.npcId === TOMAS_NPC_ID && (payload.quests?.length ?? 0) > 0) {
        this.requestBeat(TutorialStep.TAKE_QUEST);
      }
    }));

    // TAKE_QUEST dismiss — the route continues into field combat and remains
    // armed until Warden Lark accepts the completed hunt.
    sub(bus.on(EVENTS.NET_MESSAGE_QUEST_ACCEPTED, (payload) => {
      if (!this.armed || this.finished) return;
      if (payload?.quest?.id !== 'first_hunt') return;
      this.completeBeat(TutorialStep.TAKE_QUEST);
    }));

    // The reworked FTUE is complete when the opening hunt is turned in.
    sub(bus.on(EVENTS.NET_MESSAGE_QUEST_COMPLETED, (payload) => {
      if (!this.armed || this.finished || payload?.questId !== 'first_hunt') return;
      this.finish({ persist: true });
    }));

    // Window-open dismissal for the inventory prompt.
    sub(bus.on(EVENTS.UI_ACTION, (data: { action: string }) => {
      if (!this.armed || this.finished) return;
      if (data?.action === 'inventory' && this.activeBeat === TutorialStep.FIRST_LOOT) {
        this.completeBeat(TutorialStep.FIRST_LOOT);
      }
    }));

    // Legacy escape hatch (guide_rafael "I am ready to begin!") — ends the
    // tutorial outright.
    sub(bus.on(EVENTS.TUTORIAL_COMPLETE, () => {
      if (!this.finished) this.finish({ persist: true });
    }));
  }

  private arm(mapId: string): void {
    if (this.finished) return;
    if (!this.armed) {
      this.armed = true;
      this.armedAtMs = Date.now();
      // Direct field tests intentionally bypass the village lesson.
      if (mapId === FIELD_MAP_ID) this.completeVillageBeats();
      gameLogger.info('tutorial', `[TutorialSystem] Armed — on-ramp FTUE live (${mapId})`);
      return;
    }

    // Clear skipped village prompts when the normal route reaches the field
    // so combat beats surface immediately.
    if (mapId === FIELD_MAP_ID) this.completeVillageBeats();
  }

  // ── Frame poll (proximity + the MOVE nudge) ────────────────────────

  public update(_deltaTime: number, _time: number): void {
    void _deltaTime;
    void _time;
    if (!this.armed || this.finished) return;
    // Wall-clock throttle (delta units vary by caller — ms vs s).
    const now = Date.now();
    if (now - this.lastPollAtMs < POLL_INTERVAL_MS) return;
    this.lastPollAtMs = now;

    const targets = ServiceRegistry.getInstance().get('EcsCombatTargets', { optional: true });
    const player = targets?.getPlayer?.();
    const px: number | undefined = player?.position?.x ?? player?.x;
    const py: number | undefined = player?.position?.y ?? player?.y;
    if (typeof px !== 'number' || typeof py !== 'number') return;

    // MOVE — village only. Shows only if no movement input arrived
    // within MOVE_NUDGE_DELAY_MS of arming; a player who walks immediately
    // never sees it.
    if (
      this.isVillagePhase() &&
      this.phases.get(TutorialStep.MOVE) === 'pending' &&
      !this.movementInputSeen &&
      now - this.armedAtMs >= MOVE_NUDGE_DELAY_MS
    ) {
      this.requestBeat(TutorialStep.MOVE);
    }

    // ATTACK — a hostile mob wandered into the player's space.
    if (
      this.isFieldPhase() &&
      this.phases.get(TutorialStep.ATTACK) === 'pending' &&
      targets?.getHostileTargetsInRange
    ) {
      const hostiles = targets.getHostileTargetsInRange(px, py, ATTACK_TRIGGER_RADIUS_PX);
      if (hostiles.length > 0) this.requestBeat(TutorialStep.ATTACK);
    }

    // TALK_TO_NPC — within Tomas's interaction radius (matches the server's
    // talkToNPC range so "walk up and press E" always works when this shows).
    // Reads the SAME NPCManager source the interact key (PredictionLoop →
    // getNearestTalkableNpc) uses — server-spawned talkable NPCs live there,
    // not in the ECS combat bags.
    if (this.isVillagePhase() && this.phases.get(TutorialStep.TALK_TO_NPC) === 'pending') {
      const es = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
      const tomas = es?.getNPCManager?.()?.getNPCs?.()?.get?.(TOMAS_NPC_ID);
      const tx = tomas?.position?.x;
      const ty = tomas?.position?.y;
      if (
        typeof tx === 'number' && typeof ty === 'number' &&
        Math.hypot(tx - px, ty - py) <= GAMEPLAY_CONFIG.interactionRanges.npc
      ) {
        this.requestBeat(TutorialStep.TALK_TO_NPC);
      }
    }
  }

  // ── Beat state machine ─────────────────────────────────────────────

  /** Show a beat, or queue it behind the active one (one-at-a-time rule). */
  private requestBeat(step: TutorialStep): void {
    if (this.finished || this.phases.get(step) !== 'pending') return;
    if (this.activeBeat === null) {
      this.showBeat(step);
    } else {
      this.phases.set(step, 'queued');
      this.queue.push(step);
    }
  }

  private showBeat(step: TutorialStep): void {
    this.phases.set(step, 'active');
    this.activeBeat = step;
    const objective = this.objectives.get(step);
    EventBus.getInstance().emit(EVENTS.TUTORIAL_STEP_CHANGED, { step, objective });
    gameLogger.info('tutorial', `📍 FTUE beat shown: ${step}`);

    const def = BEAT_DEFS[step as Exclude<TutorialStep, TutorialStep.COMPLETED>];
    if (def?.autoDismissMs) {
      this.clearAutoDismiss();
      this.autoDismissTimer = setTimeout(() => this.completeBeat(step), def.autoDismissMs);
    }
  }

  /**
   * Mark a beat done. If it was showing, announce the completion + advance
   * the queue; a pending/queued beat completes silently (the player did the
   * thing before we ever needed to prompt for it).
   */
  private completeBeat(step: TutorialStep): void {
    const phase = this.phases.get(step);
    if (!phase || phase === 'done') return;

    const wasActive = this.activeBeat === step;
    this.phases.set(step, 'done');
    this.queue = this.queue.filter(s => s !== step);
    const objective = this.objectives.get(step);
    if (objective) objective.isCompleted = true;

    if (wasActive) {
      this.clearAutoDismiss();
      this.activeBeat = null;
      EventBus.getInstance().emit(EVENTS.TUTORIAL_OBJECTIVE_COMPLETED, { step, objective });
      gameLogger.info('tutorial', `✅ FTUE beat completed: ${step}`);
      const next = this.queue.shift();
      if (next) this.showBeat(next);
    }
  }

  private finish(opts: { persist: boolean }): void {
    if (this.finished) return;
    this.finished = true;
    this.clearAutoDismiss();
    this.queue = [];
    this.activeBeat = null;
    for (const [step, phase] of this.phases) {
      if (phase !== 'done') this.phases.set(step, 'done');
    }
    if (opts.persist) {
      try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(FTUE_DONE_KEY, '1');
      } catch { /* storage unavailable — fine, per-session only */ }
    }
    EventBus.getInstance().emit(EVENTS.TUTORIAL_STEP_CHANGED, {
      step: TutorialStep.COMPLETED,
      objective: undefined,
    });
    gameLogger.info('tutorial', '🎓 On-ramp FTUE complete!');
  }

  private completeVillageBeats(): void {
    this.completeBeat(TutorialStep.MOVE);
    this.completeBeat(TutorialStep.TALK_TO_NPC);
    this.completeBeat(TutorialStep.TAKE_QUEST);
  }

  private observeVillageMovementInput(): void {
    if (!this.armed || this.finished || !this.isVillagePhase()) return;
    this.movementInputSeen = true;
    this.completeBeat(TutorialStep.MOVE);
  }

  private isVillagePhase(): boolean {
    return this.currentMapId === VILLAGE_MAP_ID;
  }

  private isFieldPhase(): boolean {
    return this.currentMapId === FIELD_MAP_ID;
  }

  private localSessionId(): string | null {
    const nm = ServiceRegistry.getInstance().get('NetworkManager', { optional: true });
    return nm?.getRoom?.()?.sessionId ?? null;
  }

  private clearAutoDismiss(): void {
    if (this.autoDismissTimer !== null) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
  }

  public dispose(): void {
    this.clearAutoDismiss();
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    if (this.onMoveKeyDown && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onMoveKeyDown);
      this.onMoveKeyDown = null;
    }
  }
}
